import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Row, Col, Select, Checkbox, Upload, Space } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, InboxOutlined, FolderOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/Layout/MainLayout';
import SmartEditor from '../../components/Common/SmartEditor';
import { draftService } from '../../services/draftService';

const { Dragger } = Upload;

const DraftRegist: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [sendMail, setSendMail] = useState(true);
  const [sendSMS, setSendSMS] = useState(true);
  const [draftNo, setDraftNo] = useState('');
  const [receivers, setReceivers] = useState<any[]>([]);

  // 초기 데이터 로드
  useEffect(() => {
    const fetchInitData = async () => {
      try {
        const result = await draftService.getDraftInit();
        console.log('초기 데이터 응답:', result);
        if (result.resultCode === 200) {
          setDraftNo(result.data.draftNo);
          setReceivers(result.data.receivers || []);
          console.log('수신 결재자:', result.data.receivers);
        }
      } catch (error) {
        console.error('초기 데이터 로드 오류:', error);
      }
    };

    fetchInitData();
  }, []);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      // 스마트에디터 내용 추출
      const content = (window as any).getContent_smarteditor?.();

      const formData = new FormData();
      formData.append('Draft_No', draftNo);
      formData.append('Draft_Subject', values.Draft_Subject);
      formData.append('Draft_Cost', values.Draft_Cost);
      formData.append('Draft_Client', values.Draft_Client);
      formData.append('Receiver', values.Receiver || '');
      formData.append('editorTxt', content || '');
      formData.append('Send_Mail_YN', sendMail ? 'Y' : 'N');
      formData.append('Send_SMS_YN', sendSMS ? 'Y' : 'N');

      // 파일 추가
      fileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append('fileInput', file.originFileObj);
        }
      });

      console.log('제출 데이터:', values, { content, fileCount: fileList.length });

      // API 호출 (draftService 사용)
      const result = await draftService.createDraft(formData);

      if (result.resultCode === 200) {
        message.success('기안서가 등록되었습니다.');
        navigate('/draft/list');
      } else {
        message.error(result.message || '기안서 등록 실패');
      }
    } catch (error) {
      console.error('기안서 등록 오류:', error);
      message.error('기안서 등록 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleTempSave = async () => {
    try {
      setLoading(true);

      // 폼 값 가져오기 (검증 없이)
      const values = form.getFieldsValue();

      // 스마트에디터 내용 추출
      const content = (window as any).getContent_smarteditor?.();

      // 필수 필드 체크 (제목, 비용, 지출처만)
      if (!values.Draft_Subject || !values.Draft_Cost || !values.Draft_Client) {
        message.warning('제목, 비용, 지출처는 필수 입력 항목입니다.');
        return;
      }

      // FormData로 변경 (파일 업로드 지원)
      const formData = new FormData();
      formData.append('Draft_Subject', values.Draft_Subject);
      formData.append('Draft_Contents', content || '');
      formData.append('Draft_Cost', values.Draft_Cost);
      formData.append('Draft_Client', values.Draft_Client);

      // 파일 추가
      fileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append('fileInput', file.originFileObj);
        }
      });

      const response = await fetch('/acghr/acghr/api/draft/tmp/save', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const result = await response.json();

      if (result.resultCode === 200) {
        message.success(`임시저장되었습니다. (파일 ${result.data.fileCount || 0}개)`);
        navigate('/draft/list');
      } else {
        message.error(result.message || '임시저장 실패');
      }
    } catch (error) {
      console.error('임시저장 오류:', error);
      message.error('임시저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    multiple: true,
    fileList,
    beforeUpload: (file: any) => {
      const uploadFile: UploadFile = {
        uid: `${Date.now()}-${file.name}`,
        name: file.name,
        status: 'done',
        originFileObj: file,
      };
      setFileList([...fileList, uploadFile]);
      return false; // 자동 업로드 방지
    },
    onRemove: (file: UploadFile) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
  };

  return (
    <MainLayout>
      <Card
        title="기안서 등록"
        extra={
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/draft/list')}>
            목록으로
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            Draft_Cost: 0,
            Send_Mail_YN: true,
            Send_SMS_YN: true,
          }}
        >
          {/* 1줄: 기안번호, 수신자, 메일/SMS, 비용, 지출처 */}
          <Row gutter={16}>
            <Col span={3}>
              <Form.Item label="기안번호">
                <Input disabled value={draftNo} />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                label="수신(결재자)"
                name="Receiver"
                rules={[{ required: true, message: '수신자 선택' }]}
              >
                <Select
                  placeholder="선택"
                  optionLabelProp="label"
                >
                  {receivers.map((r) => (
                    <Select.Option
                      key={r.Admin_Name}
                      value={`${r.Admin_Name} ${r.Admin_Position}|${r.Admin_Email}|${r.Admin_Cell}`}
                      label={`${r.Admin_Name} ${r.Admin_Position}`}
                    >
                      {r.Admin_Name} {r.Admin_Position}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="발송 옵션">
                <Space>
                  <Checkbox checked={sendMail} onChange={(e) => setSendMail(e.target.checked)}>
                    메일
                  </Checkbox>
                  <Checkbox checked={sendSMS} onChange={(e) => setSendSMS(e.target.checked)}>
                    SMS
                  </Checkbox>
                </Space>
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                label="비용"
                name="Draft_Cost"
                rules={[{ required: true, message: '금액 필수' }]}
              >
                <Input type="number" placeholder="금액" addonAfter="원" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item label="지출처" name="Draft_Client">
                <Input placeholder="지출처" />
              </Form.Item>
            </Col>
          </Row>

          {/* 2줄: 제목 */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="제목"
                name="Draft_Subject"
                rules={[{ required: true, message: '제목을 입력해주세요' }]}
              >
                <Input placeholder="제목을 입력하세요" />
              </Form.Item>
            </Col>
          </Row>

          {/* 첨부파일 */}
          <Form.Item label="첨부파일">
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">클릭하거나 파일을 드래그하여 업로드</p>
              <p className="ant-upload-hint">
                여러 파일을 동시에 업로드할 수 있습니다.
              </p>
            </Dragger>
          </Form.Item>

          {/* 스마트에디터 */}
          <Form.Item label="내용">
            <SmartEditor id="smarteditor" height="400px" />
          </Form.Item>

          {/* 버튼 */}
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={handleTempSave} icon={<FolderOutlined />}>
                임시저장
              </Button>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
                등록
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </MainLayout>
  );
};

export default DraftRegist;
